import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Archive, Trash2, CheckCircle, RotateCcw, AlertTriangle,
  Calendar, DollarSign, FolderKanban, User
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { key: 'inquiry', label: 'Inquiry' },
  { key: 'follow_up', label: 'Follow-up' },
  { key: 'proposal_sent', label: 'Proposal Sent' },
  { key: 'contract_signed', label: 'Contract Signed' },
  { key: 'initiation', label: 'Initiation' },
  { key: 'planning', label: 'Planning' },
  { key: 'execution', label: 'Execution' },
  { key: 'monitoring', label: 'Monitoring & Controlling' },
  { key: 'closure', label: 'Closure' }
];

const ProjectArchivePage = () => {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [projectToRestore, setProjectToRestore] = useState(null);
  
  const isAdmin = user?.role === 'admin';
  
  // Get tab from URL params, default to 'archived'
  const defaultTab = searchParams.get('tab') || 'archived';

  useEffect(() => {
    fetchArchivedProjects();
  }, []);

  const fetchArchivedProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch archived projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRestoreDialog = (project) => {
    setProjectToRestore(project);
    setRestoreDialogOpen(true);
  };

  const handleRestoreToStage = async (stage) => {
    if (!projectToRestore) return;
    
    setActionLoading(projectToRestore.id);
    setRestoreDialogOpen(false);
    
    try {
      // First restore the project
      await axios.post(`${API}/projects/${projectToRestore.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then update the status to the selected stage
      await axios.patch(`${API}/projects/${projectToRestore.id}/status`, 
        { status: stage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Project restored to ${PIPELINE_STAGES.find(s => s.key === stage)?.label || stage}`);
      fetchArchivedProjects();
    } catch (error) {
      console.error('Failed to restore project:', error);
      toast.error('Failed to restore project.');
    } finally {
      setActionLoading(null);
      setProjectToRestore(null);
    }
  };

  const handlePermanentDelete = async (projectId) => {
    if (!window.confirm('PERMANENTLY DELETE this project? This cannot be undone!')) return;
    if (!window.confirm('Are you absolutely sure?')) return;
    
    setActionLoading(projectId);
    try {
      await axios.delete(`${API}/projects/${projectId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchArchivedProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project.');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      initiation: 'status-initiation',
      planning: 'status-planning',
      execution: 'status-execution',
      monitoring: 'status-monitoring',
      closure: 'status-closure',
      active: 'status-active',
      completed: 'status-completed',
      on_hold: 'status-on-hold'
    };
    return statusMap[status] || 'status-inactive';
  };

  const formatStatus = (status) => {
    const statusLabels = {
      initiation: '🎯 Initiation',
      planning: '📋 Planning',
      execution: '🚀 Execution',
      monitoring: '📊 Monitoring',
      closure: '✅ Closure',
      active: '🟢 Active',
      completed: '✨ Completed',
      on_hold: '⏸️ On Hold'
    };
    return statusLabels[status] || status;
  };

  const archivedProjects = projects.filter(p => p.is_archived && p.status !== 'completed' && !p.is_deleted);
  const completedProjects = projects.filter(p => p.status === 'completed');
  const deletedProjects = projects.filter(p => p.is_deleted);

  const ProjectCard = ({ project, showRestoreOnly = false }) => (
    <Card className="shadow-lg rounded-xl border-0 card-hover" data-testid={`archive-project-${project.id}`}>
      <CardContent className="p-4 sm:p-6">
        {/* Mobile Layout */}
        <div className="sm:hidden">
          <div className="flex items-start justify-between mb-2">
            <Link to={`/dashboard/projects/${project.id}`} className="flex-1 min-w-0 pr-2">
              {project.project_number && (
                <span className="inline-block px-2 py-0.5 rounded-full bg-fearless-blue/10 text-fearless-blue text-xs font-mono font-semibold mb-1" data-testid={`archive-project-number-mobile-${project.id}`}>
                  {project.project_number}
                </span>
              )}
              <h3 className="text-base font-bold text-carbon-black hover:text-fearless-blue transition-colors line-clamp-2">
                {project.title}
              </h3>
            </Link>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusClass(project.status)}`}>
              {formatStatus(project.status)}
            </span>
          </div>
          <p className="text-sm text-slate-gray mb-3 line-clamp-2">{project.description}</p>
          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
            <div className="flex items-center text-slate-gray">
              <DollarSign className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="font-semibold">{project.budget.toLocaleString()}</span>
            </div>
            {project.start_date && (
              <div className="flex items-center text-slate-gray">
                <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{project.start_date}</span>
              </div>
            )}
            <div className="flex items-center text-slate-gray col-span-2">
              <User className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{project.client_name}</span>
            </div>
          </div>
          {project.archived_at && (
            <p className="text-xs text-slate-gray mb-2">
              Archived: {new Date(project.archived_at).toLocaleDateString()}
            </p>
          )}
          {project.deleted_at && (
            <p className="text-xs text-red-500 mb-2">
              Deleted: {new Date(project.deleted_at).toLocaleDateString()}
            </p>
          )}
          {isAdmin && (
            <div className="flex space-x-2 pt-2 border-t border-gray-100">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openRestoreDialog(project)}
                disabled={actionLoading === project.id}
                className="border-green-300 text-green-600 hover:bg-green-50 flex-1 text-xs h-8"
                data-testid={`restore-btn-${project.id}`}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Restore
              </Button>
              {!showRestoreOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePermanentDelete(project.id)}
                  disabled={actionLoading === project.id}
                  className="border-red-300 text-red-600 hover:bg-red-50 flex-1 text-xs h-8"
                  data-testid={`delete-btn-${project.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:block">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0 pr-4">
              <Link to={`/dashboard/projects/${project.id}`}>
                {project.project_number && (
                  <span className="inline-block px-2.5 py-1 rounded-full bg-fearless-blue/10 text-fearless-blue text-xs font-mono font-semibold mb-2" data-testid={`archive-project-number-desktop-${project.id}`}>
                    {project.project_number}
                  </span>
                )}
                <h3 className="text-xl font-bold text-carbon-black mb-2 hover:text-fearless-blue transition-colors truncate">
                  {project.title}
                </h3>
              </Link>
              <p className="text-slate-gray mb-4 line-clamp-2">{project.description}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <div className="flex items-center text-slate-gray">
                  <DollarSign className="h-4 w-4 mr-1" />
                  <span className="font-semibold">{project.budget.toLocaleString()}</span>
                </div>
                {project.start_date && (
                  <div className="flex items-center text-slate-gray">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{project.start_date}</span>
                  </div>
                )}
                <div className="text-slate-gray">
                  Client: <span className="font-semibold">{project.client_name}</span>
                </div>
              </div>
              {project.archived_at && (
                <p className="text-xs text-slate-gray mt-2">
                  Archived: {new Date(project.archived_at).toLocaleDateString()}
                </p>
              )}
              {project.deleted_at && (
                <p className="text-xs text-red-500 mt-2">
                  Deleted: {new Date(project.deleted_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end space-y-3 flex-shrink-0">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${getStatusClass(project.status)}`}>
                {formatStatus(project.status)}
              </span>
              {isAdmin && (
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openRestoreDialog(project)}
                    disabled={actionLoading === project.id}
                    className="border-green-300 text-green-600 hover:bg-green-50"
                    data-testid={`restore-btn-${project.id}`}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restore
                  </Button>
                  {!showRestoreOnly && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePermanentDelete(project.id)}
                      disabled={actionLoading === project.id}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`delete-btn-${project.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Forever
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="archive-loading">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="project-archive-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1 sm:mb-2">Project Archive</h1>
            <p className="text-sm sm:text-base text-slate-gray">Manage archived and deleted projects</p>
          </div>
          <Link to="/dashboard/projects">
            <Button variant="outline" className="w-full sm:w-auto" data-testid="back-to-projects-btn">
              <FolderKanban className="h-4 w-4 mr-2" />
              Active Projects
            </Button>
          </Link>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="grid w-full grid-cols-3 min-w-[320px] sm:min-w-0">
              <TabsTrigger value="archived" className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2" data-testid="tab-archived">
                <Archive className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                <span className="truncate">Archived ({archivedProjects.length})</span>
              </TabsTrigger>
              <TabsTrigger value="completed" className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2" data-testid="tab-completed">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                <span className="truncate">Completed ({completedProjects.length})</span>
              </TabsTrigger>
              <TabsTrigger value="trash" className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2" data-testid="tab-trash">
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
                <span className="truncate">Trash ({deletedProjects.length})</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="archived" className="space-y-4 mt-4 sm:mt-6">
            {archivedProjects.length === 0 ? (
              <Card className="shadow-lg rounded-xl border-0">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Archive className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-slate-gray opacity-50" />
                  <p className="text-slate-gray text-sm sm:text-lg">No archived projects</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {archivedProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4 mt-4 sm:mt-6">
            {completedProjects.length === 0 ? (
              <Card className="shadow-lg rounded-xl border-0">
                <CardContent className="p-8 sm:p-12 text-center">
                  <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-slate-gray opacity-50" />
                  <p className="text-slate-gray text-sm sm:text-lg">No completed projects</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} showRestoreOnly />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="trash" className="space-y-4 mt-4 sm:mt-6">
            <Card className="border-yellow-300 bg-yellow-50 shadow-lg rounded-xl">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start sm:items-center space-x-2 sm:space-x-3">
                  <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <p className="text-xs sm:text-sm text-yellow-800">
                    <strong>Warning:</strong> Permanent deletion removes all data.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {deletedProjects.length === 0 ? (
              <Card className="shadow-lg rounded-xl border-0">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Trash2 className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-slate-gray opacity-50" />
                  <p className="text-slate-gray text-sm sm:text-lg">Trash is empty</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {deletedProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Restore Dialog */}
        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Restore Project</DialogTitle>
              <DialogDescription>
                Select which stage to restore "{projectToRestore?.title}" to:
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-2 mt-4 max-h-[400px] overflow-y-auto">
              {PIPELINE_STAGES.map((stage) => (
                <Button
                  key={stage.key}
                  variant="outline"
                  className="justify-start h-auto py-3 px-4 hover:bg-gray-100 hover:border-fearless-blue"
                  onClick={() => handleRestoreToStage(stage.key)}
                  disabled={actionLoading === projectToRestore?.id}
                >
                  <div className="text-left">
                    <div className="font-semibold">{stage.label}</div>
                  </div>
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ProjectArchivePage;
